import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { animation, borderRadius, breakpoints, spacing } from '@naf/theme';
import { Text } from '@naf/text';
import { nafColor } from '@nafcore/theme';
import { Variant } from './CombinedResultCard';
import { HoverWrap } from '../../blocks/MultiRowArticleBlock';

export const Top = styled.div<{ $variant: Variant }>`
  flex-grow: 1;
  position: relative;
  padding: ${spacing.space16} ${spacing.space16} 0;

  ${({ $variant }) =>
    $variant === 'card'
      ? css`
          border-radius: ${borderRadius.L};
        `
      : css`
          border-radius: ${borderRadius.M};
        `};
`;

export const InfoContainer = styled.div<{ $variant: Variant }>`
  border: 2px solid ${({ theme }: { theme: any }) => theme.bg};
  ${({ $variant }) =>
    $variant === 'card'
      ? css`
          margin-top: -8px;
          border-top: 0;
          border-radius: ${borderRadius.L};
        `
      : css`
          border-radius: ${borderRadius.M};
        `};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  transition: border-color 0.3s ${animation.easeIn};
`;

export const ImageContainer = styled.div<{ $variant: Variant }>`
  min-width: 100%;
  z-index: 2;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  background-color: ${({ theme }: { theme: any }) => theme.bg};
  transition: background-color 0.6s ${animation.easeIn};

  ${({ $variant }) =>
    $variant === 'card' &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top-right-radius: ${borderRadius.L};
      border-top-left-radius: ${borderRadius.L};
    `};

  img {
    ${({ $variant }) =>
      $variant === 'card' &&
      css`
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-top-right-radius: ${borderRadius.L};
        border-top-left-radius: ${borderRadius.L};
      `};
    height: 100%;
    width: 100%;
    object-fit: cover;
    transition: transform 0.6s ${animation.easeIn};
  }
`;

export const DataContainer = styled.div<{ $variant: Variant }>`
  ${({ $variant }) =>
    $variant === 'result' &&
    css`
      display: flex;
      > div {
        width: 50%;
      }

      @media (max-width: ${breakpoints.m}) {
        display: block;
        > div {
          width: unset;
        }
      }
    `}
`;

export const StatedInfo = styled.div<{ $variant: Variant }>`
  border-top: 1px solid ${({ theme }: { theme: any }) => theme.bg};
  padding: ${({ $variant }) => ($variant === 'result' ? spacing.space16 : spacing.space24)} ${spacing.space16};
  transition: border-color 0.3s ${animation.easeIn};

  > div:not(:last-child) {
    margin-bottom: ${({ $variant }) => ($variant === 'result' ? spacing.space16 : spacing.space8)};
  }
`;

export const TestedInfo = styled.div<{ $variant: Variant }>`
  position: relative;
  background-color: ${({ theme }: { theme: any }) => theme.bg};
  padding: ${({ $variant }) => ($variant === 'result' ? spacing.space16 : spacing.space24)} ${spacing.space16};
  transition: background-color 0.3s ${animation.easeIn};

  > div:not(:last-child) {
    margin-bottom: ${({ $variant }) => ($variant === 'result' ? spacing.space16 : spacing.space8)};
  }
`;

export const Header = styled(Text)<{ layoutVariant: Variant }>`
  margin-top: 0;
  flex-grow: 1;

  ${({ layoutVariant }) =>
    layoutVariant === 'result' &&
    css`
      margin-bottom: ${spacing.space8};
    `};

  @media (max-width: ${breakpoints.m}) {
    height: auto;
  }
`;

export const NafBadge = styled(Text)<{ layoutVariant: Variant }>`
  position: absolute;
  ${({ layoutVariant }) =>
    layoutVariant === 'card'
      ? css`
          top: -${spacing.space8};
        `
      : css`
          top: 2px;
          left: ${spacing.space16};
        `}
  width: max-content;
  background-color: ${({ theme }: { theme: any }) => theme.bgBadge};
  padding: 4px;
  text-transform: uppercase;
  border-radius: ${borderRadius.S};
  font-weight: bold;
  color: ${({ theme }: { theme: any }) => theme.badgeTextColor};
  z-index: 3;
`;

// `to` is here for the polymorphic `as={Link}` usage — Emotion's `as` (unlike
// styled-components) doesn't infer the target component's props. is-prop-valid strips it
// from the DOM when rendered as a plain div.
export const Card = styled.div<{ $variant: Variant; to?: string }>`
  ${({ $variant }) =>
    $variant === 'card'
      ? css`
          border-radius: ${borderRadius.L};
        `
      : css`
          border-radius: ${borderRadius.M};
        `};

  position: relative;
  padding-top: ${({ $variant }) => ($variant === 'card' ? 0 : spacing.space12)};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  ${({ $variant }) =>
    $variant === 'result' &&
    css`
      margin-bottom: ${spacing.space48};
    `}

  ${({ $variant, theme }) =>
    $variant === 'card' &&
    css`
      transition: color 0.3s ${animation.easeIn};
      :hover {
        color: ${nafColor.signature.black};

        ${TestedInfo} {
          background-color: ${(theme as any).hover};
        }

        ${InfoContainer}, ${StatedInfo} {
          border-color: ${(theme as any).hover};
        }

        ${HoverWrap} {
          background-size: 100% 2px;
        }

        ${ImageContainer} {
          background-color: ${(theme as any).hover};

          img {
            transform: scale(105%);
          }
        }

        ${NafBadge} {
          color: ${(theme as any).badgeTextColor};
        }
      }
    `}
`;
